/**
 * @fileoverview Expandable mapping-summary panel for the import-wizard field-mapping step.
 * @module import-editor/components
 *
 * Responsibilities:
 * - Render a compact always-visible stats bar: mapped count, target-field count,
 *   unmapped source-column count, and an AI-suggest trigger button.
 * - Toggle an inline tabbed detail area below the stats bar.
 * - Host four information tabs to assist the mapping workflow:
 *     1. Sample Data  — per-column sample values from the parsed file
 *     2. AI Suggestions — pending AI / rule-based suggestions with accept / dismiss
 *     3. Ignored Columns — source columns not yet assigned to any target field
 *     4. Column Groups — AI-interpreted column groups with accept / dismiss (Phase 5.4)
 *
 * Dependencies:
 * - React (useState)
 * - lucide-react icons
 * - MappingSuggestion type (from ./FieldMappingsTabContent)
 * - getTargetFieldLabel (from ../utils/targetFieldOptions)
 * - ImportDataMode type (from @firesquirrel/local-db)
 *
 * Architecture:
 * - Presentational component: all data arrives via props; no API calls, no Dexie.
 * - Internal state is limited to `isExpanded` (boolean) and `activeTab` (union literal).
 * - Safe to render in both system-web template editor and tenant-web run wizard.
 * - The "Suggest with AI" button stops propagation on click to prevent toggling the panel.
 *
 * Notes:
 * - Tab content area is capped at 220 px with overflow-y scroll to preserve grid real estate.
 * - A numeric badge in the header row indicates pending AI suggestions while panel is collapsed.
 * - `totalTargetFields` is optional — omit it when the parent has no fixed catalog (template editor).
 */

import { useState } from 'react';
import {
  Check,
  Info,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Loader2,
  X,
  CheckCheck,
  Database,
} from 'lucide-react';
import type { ImportDataMode } from '@firesquirrel/local-db';
import type { MappingSuggestion } from './FieldMappingsTabContent';
import type { GroupInterpretation } from '../unifiedWizard/services';
import { getTargetFieldLabel } from '../utils/targetFieldOptions';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * @contract MappingSummaryPanelProps
 *
 * Purpose:
 * - Drives the expandable mapping-summary panel rendered above the catalog table.
 *
 * Fields:
 * - mappedCount: fields that have a source column or a constant default value
 * - totalTargetFields: optional catalog size; omit for free-form mapping tables
 * - availableColumns: all source columns from the parsed file
 * - unmappedSourceColumns: source columns not yet assigned to any target field
 * - sampleRows: raw row objects from the parsed sample (may be empty before parse)
 * - pendingSuggestions: AI / rule suggestions not yet accepted or dismissed
 * - suggestLoading: true while the AI-suggest call is in flight
 * - suggestError: error message from the last failed suggest call
 * - aiWarning: amber informational note (e.g. AI tier-2 failure while tier-1 succeeded)
 * - onSuggest: fires when the user clicks "Suggest with AI"
 * - onAcceptSuggestion: accept one suggestion
 * - onDismissSuggestion: dismiss one suggestion
 * - onAcceptAllHigh: accept all High-confidence suggestions at once
 * - disabled: disables all action buttons
 * - dataMode: import data mode for human-readable target-field labels
 *
 * Rules:
 * - onSuggest is required only when the parent provides a suggestMappings adapter
 * - sampleRows may be empty before a file is parsed; tabs handle the empty state
 *
 * Sync:
 * - Presentational only; no sync or Dexie interaction
 */
export interface MappingSummaryPanelProps {
  mappedCount: number;
  totalTargetFields?: number;
  availableColumns: string[];
  unmappedSourceColumns: string[];
  sampleRows: Record<string, unknown>[];
  pendingSuggestions: MappingSuggestion[];
  suggestLoading?: boolean;
  suggestError?: string | null;
  aiWarning?: string | null;
  onSuggest?: () => void;
  onAcceptSuggestion?: (s: MappingSuggestion) => void;
  onDismissSuggestion?: (s: MappingSuggestion) => void;
  onAcceptAllHigh?: () => void;
  disabled?: boolean;
  dataMode: ImportDataMode;
  // Phase 5.4: Column group interpretation
  groupInterpretations?: GroupInterpretation[];
  groupInterpretLoading?: boolean;
  groupInterpretError?: string | null;
  onInterpretGroups?: () => void;
  onAcceptGroupInterpretation?: (g: GroupInterpretation) => void;
  onDismissGroupInterpretation?: (g: GroupInterpretation) => void;
}

type ActiveTab = 'sampleData' | 'aiSuggestions' | 'ignoredColumns' | 'columnGroups';

// ---------------------------------------------------------------------------
// Exported helper: sampleValuesFor
// Exported so unit tests can import it directly without a React environment.
// ---------------------------------------------------------------------------

/**
 * Extract up to `max` distinct, non-empty string values for `col` from `rows`.
 * Returns an empty array when the column has no populated cells in the sample.
 */
export function sampleValuesFor(
  rows: Record<string, unknown>[],
  col: string,
  max = 3,
): string[] {
  if (!col || !rows.length) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of rows) {
    const v = r?.[col];
    if (v === null || v === undefined || v === '') continue;
    const s = String(v);
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
    if (out.length >= max) break;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Local sub-component: ConfidenceBadge
// ---------------------------------------------------------------------------

function ConfidenceBadge({ confidence }: { confidence: number }) {
  if (confidence >= 0.8) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-green-100 text-green-700">
        High
      </span>
    );
  }
  if (confidence >= 0.5) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-yellow-100 text-yellow-700">
        Review
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-gray-100 text-gray-500">
      Low
    </span>
  );
}

// ---------------------------------------------------------------------------
// Local sub-component: TabButton
// ---------------------------------------------------------------------------

function TabButton({
  label,
  count,
  icon,
  active,
  onClick,
}: {
  label: string;
  count?: number;
  icon?: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors shrink-0',
        active
          ? 'border-orange-500 text-orange-700'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
      ].join(' ')}
    >
      {icon}
      {label}
      {count !== undefined && count > 0 && (
        <span
          className={[
            'inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-semibold px-1',
            active ? 'bg-orange-100 text-orange-700' : 'bg-gray-200 text-gray-600',
          ].join(' ')}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Local sub-component: SampleDataTab
// Renders a spreadsheet-style grid: sticky column-header row + one row per
// sample record, matching the preview table on the Sample Data wizard step.
// ---------------------------------------------------------------------------

/**
 * Coerce a cell value to a display string.
 * Objects / arrays are JSON-stringified; primitives are cast with String().
 */
function cellToString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  return String(value);
}

function SampleDataTab({
  availableColumns,
  sampleRows,
}: {
  availableColumns: string[];
  sampleRows: Record<string, unknown>[];
}) {
  if (availableColumns.length === 0) {
    return (
      <div className="px-4 py-6 text-center text-xs text-gray-400">
        No sample data available. Upload a file on the Sample Data step to populate this panel.
      </div>
    );
  }

  if (sampleRows.length === 0) {
    return (
      <div className="px-4 py-6 text-center text-xs text-gray-400">
        {availableColumns.length} column{availableColumns.length !== 1 ? 's' : ''} detected &mdash; no sample rows available.
      </div>
    );
  }

  return (
    <table className="min-w-full text-xs text-left border-separate border-spacing-0">
      <thead className="bg-gray-100 sticky top-0 z-10">
        <tr>
          {/* Row-number gutter */}
          <th className="px-2 py-1.5 font-medium text-gray-400 text-right w-8 border-b border-gray-200 bg-gray-100 select-none">
            #
          </th>
          {availableColumns.map(col => (
            <th
              key={col}
              className="px-2 py-1.5 font-medium whitespace-nowrap text-gray-700 border-b border-gray-200 bg-gray-100"
              title={col}
            >
              {col.length > 28 ? `${col.slice(0, 28)}\u2026` : col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sampleRows.map((row, rowIdx) => (
          <tr
            key={rowIdx}
            className={rowIdx % 2 === 1 ? 'bg-gray-50/60' : undefined}
          >
            <td className="px-2 py-1 text-gray-400 text-right font-mono border-b border-gray-100 select-none">
              {rowIdx + 1}
            </td>
            {availableColumns.map(col => {
              const full = cellToString(row?.[col]);
              const truncated = full.length > 60 ? `${full.slice(0, 60)}\u2026` : full;
              return (
                <td
                  key={col}
                  className="px-2 py-1 font-mono whitespace-nowrap max-w-[200px] truncate text-gray-700 border-b border-gray-100"
                  title={full || undefined}
                >
                  {truncated || <span className="text-gray-300">&mdash;</span>}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Local sub-component: AiSuggestionsTab
// ---------------------------------------------------------------------------

function AiSuggestionsTab({
  suggestions,
  suggestLoading,
  disabled,
  dataMode,
  onAccept,
  onDismiss,
  onAcceptAllHigh,
  hasSuggester,
}: {
  suggestions: MappingSuggestion[];
  suggestLoading: boolean;
  disabled: boolean;
  dataMode: ImportDataMode;
  onAccept?: (s: MappingSuggestion) => void;
  onDismiss?: (s: MappingSuggestion) => void;
  onAcceptAllHigh?: () => void;
  hasSuggester: boolean;
}) {
  const hasHighSuggestions = suggestions.some(s => s.confidence >= 0.8);

  if (suggestLoading) {
    return (
      <div className="flex items-center justify-center gap-2 px-4 py-6 text-xs text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
        Analyzing your columns\u2026
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <div className="px-4 py-5 text-center">
        <p className="text-xs text-gray-500">No pending suggestions.</p>
        {hasSuggester && (
          <p className="text-[11px] text-gray-400 mt-1">
            Click{' '}
            <span className="font-medium text-orange-600">Suggest with AI</span>{' '}
            above to generate mapping recommendations.
          </p>
        )}
      </div>
    );
  }

  return (
    <div>
      {hasHighSuggestions && onAcceptAllHigh && (
        <div className="px-3 py-2 flex items-center justify-between border-b border-gray-100 bg-gray-50/50">
          <p className="text-xs text-gray-600">
            {suggestions.length} pending suggestion{suggestions.length !== 1 ? 's' : ''}
          </p>
          <button
            type="button"
            disabled={disabled}
            onClick={onAcceptAllHigh}
            data-testid="accept-all-high-btn"
            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-sm transition-colors disabled:opacity-60"
          >
            <CheckCheck className="h-3 w-3" /> Accept All High
          </button>
        </div>
      )}
      {suggestions.map((s, index) => {
        const cols = s.sourceColumns ?? [s.sourceColumn];
        const showConcatStyle = s.transform === 'concat' && cols.length > 1;
        const sourceLabel = showConcatStyle
          ? cols.join(s.concatSeparator ?? ' ')
          : cols[0]!;
        return (
          <div
            key={`${s.targetField}-${index}`}
            className="px-3 py-2 flex items-center gap-2 border-b border-gray-50 last:border-b-0"
          >
            <ConfidenceBadge confidence={s.confidence} />
            <div className="flex-1 min-w-0 text-xs">
              <span className="font-mono text-gray-800">{sourceLabel}</span>
              <span className="mx-1.5 text-gray-400">\u2192</span>
              <span className="font-medium text-gray-700">
                {getTargetFieldLabel(s.targetField, dataMode)}
              </span>
              {s.transform && s.transform !== 'none' && (
                <span className="ml-1.5 text-gray-400">({s.transform})</span>
              )}
            </div>
            {s.reasoning && (
              <p
                className="text-[10px] text-gray-400 truncate max-w-[140px] hidden md:block"
                title={s.reasoning}
              >
                {s.reasoning}
              </p>
            )}
            <div className="flex items-center gap-1 shrink-0">
              {onAccept && (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onAccept(s)}
                  className="p-1 rounded-sm text-green-600 hover:bg-green-100 disabled:opacity-60"
                  title="Accept this suggestion"
                  aria-label={`Accept suggestion for ${sourceLabel}`}
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
              )}
              {onDismiss && (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onDismiss(s)}
                  className="p-1 rounded-sm text-gray-400 hover:bg-gray-200 disabled:opacity-60"
                  title="Dismiss this suggestion"
                  aria-label={`Dismiss suggestion for ${sourceLabel}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Local sub-component: IgnoredColumnsTab
// ---------------------------------------------------------------------------

function IgnoredColumnsTab({
  unmappedSourceColumns,
}: {
  unmappedSourceColumns: string[];
}) {
  if (unmappedSourceColumns.length === 0) {
    return (
      <div className="px-4 py-6 text-center text-xs text-gray-500">
        All source columns have been assigned to a target field.
      </div>
    );
  }
  return (
    <div className="px-3 py-3">
      <div className="flex flex-wrap gap-1.5">
        {unmappedSourceColumns.map(c => (
          <span
            key={c}
            className="font-mono bg-amber-50 border border-amber-200 text-amber-900 rounded-sm px-1.5 py-0.5 text-[11px]"
          >
            {c}
          </span>
        ))}
      </div>
      <p className="mt-2.5 text-[11px] text-amber-800">
        These columns are present in your file but will not be written to any entity field.
        Map them above, add a custom target field, or click{' '}
        <span className="font-medium">Suggest with AI</span> to pick them up automatically.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Local sub-component: GroupInterpretationsTab (Phase 5.4)
// Renders AI-interpreted column groups with per-column mappings, reasoning,
// confidence badges, and accept/dismiss actions.
// ---------------------------------------------------------------------------

function GroupInterpretationsTab({
  interpretations,
  loading,
  error,
  disabled,
  hasInterpreter,
  onInterpret,
  onAccept,
  onDismiss,
}: {
  interpretations: GroupInterpretation[];
  loading: boolean;
  error: string | null;
  disabled: boolean;
  hasInterpreter: boolean;
  onInterpret?: () => void;
  onAccept?: (g: GroupInterpretation) => void;
  onDismiss?: (g: GroupInterpretation) => void;
}) {
  if (loading) {
    return (
      <div className="px-4 py-6 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Interpreting column groups…
      </div>
    );
  }

  if (interpretations.length === 0) {
    return (
      <div className="px-4 py-6 text-center text-xs text-gray-400">
        {error && (
          <p className="text-xs text-red-600 mb-2" role="alert">{error}</p>
        )}
        No column group interpretations yet.
        {hasInterpreter && onInterpret && (
          <>
            <br />
            <button
              type="button"
              onClick={onInterpret}
              disabled={disabled}
              className="mt-2 text-orange-600 hover:text-orange-700 font-medium disabled:opacity-60"
            >
              {error ? 'Retry' : 'Interpret column groups with AI'}
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="px-3 py-3 space-y-3">
      {error && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5" role="status">
          {error}
        </p>
      )}
      {onInterpret && (
        <button
          type="button"
          onClick={onInterpret}
          disabled={disabled}
          className="text-xs text-orange-600 hover:text-orange-700 font-medium disabled:opacity-60 mb-1"
        >
          Re-run interpretation
        </button>
      )}
      {interpretations.map((group, idx) => (
        <div
          key={`${group.groupType}-${idx}`}
          className="rounded-md border border-gray-200 bg-gray-50/50 p-3"
        >
          {/* Group header */}
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-800">
                {group.groupType}
              </span>
              <ConfidenceBadge confidence={group.confidence} />
            </div>
            <div className="flex items-center gap-1">
              {onAccept && (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onAccept(group)}
                  className="p-1 rounded-sm text-green-600 hover:bg-green-100 disabled:opacity-60"
                  title="Accept all mappings in this group"
                  aria-label={`Accept ${group.groupType} group interpretation`}
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                </button>
              )}
              {onDismiss && (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onDismiss(group)}
                  className="p-1 rounded-sm text-gray-400 hover:bg-gray-200 disabled:opacity-60"
                  title="Dismiss this group interpretation"
                  aria-label={`Dismiss ${group.groupType} group interpretation`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Group columns */}
          <div className="flex flex-wrap gap-1 mb-2">
            {group.groupColumns.map(col => (
              <span
                key={col}
                className="font-mono bg-white border border-gray-200 text-gray-700 rounded-sm px-1.5 py-0.5 text-[10px]"
              >
                {col}
              </span>
            ))}
          </div>

          {/* Per-column mappings */}
          <div className="space-y-1 mb-2">
            {group.mappings.map(m => (
              <div key={m.sourceColumn} className="flex items-center gap-2 text-[11px]">
                <span className="font-mono text-gray-600">{m.sourceColumn}</span>
                <span className="text-gray-400">→</span>
                <span className="font-medium text-gray-800">{m.targetField}</span>
                <ConfidenceBadge confidence={m.confidence} />
              </div>
            ))}
          </div>

          {/* Reasoning */}
          {group.reasoning && (
            <p className="text-[11px] text-gray-500 italic mt-1">
              {group.reasoning}
            </p>
          )}

          {/* Suggested transforms */}
          {group.suggestedTransforms && group.suggestedTransforms.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <p className="text-[10px] font-medium text-gray-600 mb-1">Suggested transforms:</p>
              {group.suggestedTransforms.map((t, tIdx) => (
                <div key={tIdx} className="text-[11px] text-gray-600">
                  <span className="font-mono bg-orange-50 text-orange-800 rounded-sm px-1">{t.type}</span>
                  {' '}
                  {t.sourceColumns.join(' + ')} → {t.targetField}
                  {t.instruction && <span className="text-gray-400 italic"> ({t.instruction})</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component: MappingSummaryPanel
// ---------------------------------------------------------------------------

/**
 * @component MappingSummaryPanel
 *
 * Purpose:
 * - Provide a compact, always-visible stats bar above the mapping catalog table
 *   that can expand into a 3-tab detail panel for deeper mapping assistance.
 *
 * Props:
 * - See MappingSummaryPanelProps above.
 *
 * Behavior:
 * - Stats bar is always rendered in collapsed state.
 * - Clicking "Mapping Tools" button toggles the expanded tabbed panel.
 * - Clicking the pending-suggestions badge in the header auto-opens the AI tab.
 * - "Suggest with AI" button stops event propagation to avoid panel toggle.
 * - Expanded panel shows 3 tabs: Sample Data | AI Suggestions (N) | Ignored Columns (N).
 * - Tab content is capped at 220 px with overflow-y scroll.
 *
 * Data Source:
 * - local (all data via props — no reads or writes)
 *
 * Constraints:
 * - No API calls, no Dexie reads/writes
 * - Shared between RunWizardColumnMappingPanel and FieldMappingsTabContent
 *
 * Performance:
 * - Internal state limited to isExpanded and activeTab — no heavy derivations
 */
export function MappingSummaryPanel({
  mappedCount,
  totalTargetFields,
  availableColumns,
  unmappedSourceColumns,
  sampleRows,
  pendingSuggestions,
  suggestLoading = false,
  suggestError = null,
  aiWarning = null,
  onSuggest,
  onAcceptSuggestion,
  onDismissSuggestion,
  onAcceptAllHigh,
  disabled = false,
  dataMode,
  groupInterpretations = [],
  groupInterpretLoading = false,
  groupInterpretError = null,
  onInterpretGroups,
  onAcceptGroupInterpretation,
  onDismissGroupInterpretation,
}: MappingSummaryPanelProps) {
  /**
   * @storage isExpanded
   * Type: Memory
   * Schema: boolean
   * Sync Role: UI-only toggle; not persisted
   */
  const [isExpanded, setIsExpanded] = useState(false);

  /**
   * @storage activeTab
   * Type: Memory
   * Schema: 'sampleData' | 'aiSuggestions' | 'ignoredColumns' | 'columnGroups'
   * Sync Role: UI-only; not persisted
   */
  const [activeTab, setActiveTab] = useState<ActiveTab>('sampleData');

  const openTab = (tab: ActiveTab) => {
    setIsExpanded(true);
    setActiveTab(tab);
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">

      {/* ---- Always-visible stats header ---- */}
      <div className="px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap">

        {/* Left: stat badges */}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-50 border border-green-200 text-green-800">
            <Check className="h-3 w-3" />
            {mappedCount} mapped
          </div>
          {totalTargetFields !== undefined && (
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-gray-50 border border-gray-200 text-gray-700">
              {totalTargetFields} target fields
            </div>
          )}
          {availableColumns.length > 0 && (
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800">
              <Info className="h-3 w-3" />
              {unmappedSourceColumns.length} of {availableColumns.length} source columns unmapped
            </div>
          )}
          <div
            className="inline-flex items-center gap-1 text-[10px] text-gray-500"
            title="In the source-column picker, a checkmark next to a column name means it's already used in another mapping row."
          >
            <span className="text-gray-400">\u2713</span>
            <span>= used elsewhere</span>
          </div>
        </div>

        {/* Right: pending-suggestions badge, AI-suggest button, expand toggle */}
        <div className="flex items-center gap-2 shrink-0">

          {/* Pending suggestions indicator — click to jump straight to AI tab */}
          {pendingSuggestions.length > 0 && !isExpanded && (
            <button
              type="button"
              onClick={() => openTab('aiSuggestions')}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-orange-700 bg-orange-50 border border-orange-200 hover:bg-orange-100 rounded-full transition-colors"
              title="Click to review pending AI suggestions"
            >
              <Sparkles className="h-3 w-3" />
              {pendingSuggestions.length} suggestion{pendingSuggestions.length !== 1 ? 's' : ''}
            </button>
          )}

          {/* Phase 5.4: Group interpretations indicator — click to jump to Column Groups tab */}
          {groupInterpretations.length > 0 && !isExpanded && (
            <button
              type="button"
              onClick={() => openTab('columnGroups')}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-orange-700 bg-orange-50 border border-orange-200 hover:bg-orange-100 rounded-full transition-colors"
              title="Click to review column group interpretations"
            >
              <Sparkles className="h-3 w-3" />
              {groupInterpretations.length} group{groupInterpretations.length !== 1 ? 's' : ''}
            </button>
          )}

          {/* AI suggest button */}
          {onSuggest && availableColumns.length > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onSuggest(); }}
              disabled={disabled || suggestLoading}
              data-testid="suggest-mappings-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-60 rounded-md transition-colors shrink-0"
              title="Use AI + rule-based matching to suggest target fields for unmapped columns"
            >
              {suggestLoading
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Sparkles className="h-3.5 w-3.5" />}
              {suggestLoading ? 'Analyzing\u2026' : 'Suggest with AI'}
            </button>
          )}

          {/* Phase 5.4: Interpret Column Groups button */}
          {onInterpretGroups && availableColumns.length > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onInterpretGroups(); }}
              disabled={disabled || groupInterpretLoading}
              data-testid="interpret-groups-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 disabled:opacity-60 rounded-md transition-colors border border-orange-200 shrink-0"
              title="Use AI to interpret groups of related columns (e.g. address, owner) together"
            >
              {groupInterpretLoading
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Sparkles className="h-3.5 w-3.5" />}
              {groupInterpretLoading ? 'Interpreting\u2026' : 'Interpret Groups'}
            </button>
          )}

          {/* Expand / collapse toggle */}
          <button
            type="button"
            onClick={() => setIsExpanded(prev => !prev)}
            data-testid="mapping-tools-toggle"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors border border-gray-200"
            title={isExpanded ? 'Collapse mapping tools' : 'Expand mapping tools'}
          >
            {isExpanded
              ? <ChevronUp className="h-3.5 w-3.5" />
              : <ChevronDown className="h-3.5 w-3.5" />}
            <span className="hidden sm:inline">Mapping Tools</span>
          </button>
        </div>
      </div>

      {/* Contextual sub-header messages */}
      {suggestError && (
        <p className="px-3 pb-2 text-xs text-red-600" role="alert">{suggestError}</p>
      )}
      {groupInterpretError && !suggestError && (
        <p className="px-3 pb-2 text-xs text-amber-700" role="status">
          <span className="font-medium">Group interpretation:</span> {groupInterpretError}
        </p>
      )}
      {aiWarning && !suggestError && (
        <p
          className="px-3 pb-2 text-xs text-amber-700"
          data-testid="ai-matcher-error"
          role="status"
        >
          <span className="font-medium">AI matcher unavailable:</span> {aiWarning}.
          Showing rule-based suggestions only.
        </p>
      )}

      {/* ---- Expandable tabbed panel ---- */}
      {isExpanded && (
        <div className="border-t border-gray-100">

          {/* Tab bar */}
          <div className="flex border-b border-gray-200 bg-gray-50/50 px-1 overflow-x-auto">
            <TabButton
              label="Sample Data"
              icon={<Database className="h-3 w-3" />}
              active={activeTab === 'sampleData'}
              onClick={() => setActiveTab('sampleData')}
            />
            <TabButton
              label="AI Suggestions"
              count={pendingSuggestions.length}
              icon={<Sparkles className="h-3 w-3" />}
              active={activeTab === 'aiSuggestions'}
              onClick={() => setActiveTab('aiSuggestions')}
            />
            <TabButton
              label="Ignored Columns"
              count={unmappedSourceColumns.length}
              icon={<Info className="h-3 w-3" />}
              active={activeTab === 'ignoredColumns'}
              onClick={() => setActiveTab('ignoredColumns')}
            />
            {onInterpretGroups && (
              <TabButton
                label="Column Groups"
                count={groupInterpretations.length}
                icon={<Sparkles className="h-3 w-3" />}
                active={activeTab === 'columnGroups'}
                onClick={() => setActiveTab('columnGroups')}
              />
            )}
          </div>

          {/* Tab content — single scroll container: both axes.
               Horizontal scrollbar stays pinned to the bottom of this
               box so it is always visible regardless of vertical position. */}
          <div className="overflow-auto" style={{ maxHeight: '280px' }}>
            {activeTab === 'sampleData' && (
              <SampleDataTab
                availableColumns={availableColumns}
                sampleRows={sampleRows}
              />
            )}
            {activeTab === 'aiSuggestions' && (
              <AiSuggestionsTab
                suggestions={pendingSuggestions}
                suggestLoading={suggestLoading}
                disabled={disabled}
                dataMode={dataMode}
                onAccept={onAcceptSuggestion}
                onDismiss={onDismissSuggestion}
                onAcceptAllHigh={onAcceptAllHigh}
                hasSuggester={!!onSuggest}
              />
            )}
            {activeTab === 'ignoredColumns' && (
              <IgnoredColumnsTab
                unmappedSourceColumns={unmappedSourceColumns}
              />
            )}
            {activeTab === 'columnGroups' && (
              <GroupInterpretationsTab
                interpretations={groupInterpretations}
                loading={groupInterpretLoading}
                error={groupInterpretError}
                disabled={disabled}
                hasInterpreter={!!onInterpretGroups}
                onInterpret={onInterpretGroups}
                onAccept={onAcceptGroupInterpretation}
                onDismiss={onDismissGroupInterpretation}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
